import { normalizeProjectStatuses } from "@grafit/shared/domain/project/project-status-helpers";
import { grafitUserSchema } from "@grafit/shared/domain/user/user.validation";
import { toUtcCalendarDate } from "@grafit/shared/utils/date-time-format";
import { isoDateOnlySchema } from "@grafit/shared/utils/validation";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { z } from "zod";

export const CHRONO_DEVIS_STATUS_FILTER_VALUES = ["WON"] as const;

type ChronoDevisStatusFilterValue = (typeof CHRONO_DEVIS_STATUS_FILTER_VALUES)[number];

const isChronoDevisStatusFilterValue = (status: string): status is ChronoDevisStatusFilterValue =>
  (CHRONO_DEVIS_STATUS_FILTER_VALUES as readonly string[]).includes(status);

/** Accepts legacy codes (e.g. `ACCEPTED`) from old bookmarks and maps them to the current filter values. */
const presetStatusFilterSchema = z
  .array(z.string())
  .transform((values) => normalizeProjectStatuses(values).filter(isChronoDevisStatusFilterValue));

export const chronoDevisSearchSchema = z.object({
  presetDateStart: isoDateOnlySchema.optional(),
  presetDateEnd: isoDateOnlySchema.optional(),
  presetStatusFilter: presetStatusFilterSchema.optional(),
  presetOwnerIds: z.array(grafitUserSchema.shape.id).min(1).optional(),
});

export type ChronoDevisSearchFilters = z.infer<typeof chronoDevisSearchSchema>;

function resolvePresetDateRange({
  presetDateStart,
  presetDateEnd,
}: Pick<ChronoDevisSearchFilters, "presetDateStart" | "presetDateEnd">): { from?: Date; to?: Date } | undefined {
  if (!presetDateStart && !presetDateEnd) {
    return undefined;
  }

  return {
    from: presetDateStart ? toUtcCalendarDate(presetDateStart) : undefined,
    to: presetDateEnd ? toUtcCalendarDate(presetDateEnd) : undefined,
  };
}

export function buildChronoDevisSearchColumnFilters({
  presetStatusFilter,
  presetDateStart,
  presetDateEnd,
}: ChronoDevisSearchFilters): ColumnFiltersState | undefined {
  const filters: ColumnFiltersState = [];
  const presetDateRange = resolvePresetDateRange({
    presetDateStart,
    presetDateEnd,
  });

  if (presetDateRange) {
    filters.push({
      id: "awardedDate",
      value: presetDateRange,
    });
  }

  if (presetStatusFilter && presetStatusFilter.length > 0) {
    filters.push({
      id: "status",
      value: presetStatusFilter,
    });
  }

  return filters.length > 0 ? filters : undefined;
}
